import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import {
  AdminState,
  DashboardStats,
  Product,
  Order,
  UserRole,
  CreateProductRequest,
  UpdateProductRequest,
  OrderStatus,
  transformApiUserToFrontend
} from '../../types';

export const fetchDashboardStats = createAsyncThunk<DashboardStats, void>(
  'admin/fetchDashboardStats',
  async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  }
);

export const fetchDashboardStatsFiltered = createAsyncThunk<DashboardStats, { startDate?: string; endDate?: string }>(
  'admin/fetchDashboardStatsFiltered',
  async ({ startDate, endDate }) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/admin/stats?${params.toString()}`);
    return response.data;
  }
);

export const fetchAdminProducts = createAsyncThunk<any, { page?: number; size?: number }>(
  'admin/fetchProducts',
  async ({ page = 0, size = 10 }) => {
    const response = await api.get(`/admin/products?page=${page + 1}&per_page=${size}`);
    return response.data;
  }
);

export const createProduct = createAsyncThunk<Product, CreateProductRequest>(
  'admin/createProduct',
  async (productData) => {
    const response = await api.post('/admin/products', productData);
    return response.data;
  }
);

export const updateProduct = createAsyncThunk<Product, { productId: number; data: UpdateProductRequest }>(
  'admin/updateProduct',
  async ({ productId, data }) => {
    const response = await api.put(`/admin/products/${productId}`, data);
    return response.data;
  }
);

export const fetchProductById = createAsyncThunk<Product, number>(
  'admin/fetchProductById',
  async (productId) => {
    const response = await api.get(`/admin/products/${productId}`);
    return response.data.product;
  }
);

export const deleteProduct = createAsyncThunk<number, number>(
  'admin/deleteProduct',
  async (productId) => {
    await api.delete(`/admin/products/${productId}`);
    return productId;
  }
);

export const fetchAdminOrders = createAsyncThunk<any, { page?: number; size?: number; status?: string }>(
  'admin/fetchOrders',
  async ({ page = 0, size = 10, status }) => {
    const params = new URLSearchParams({
      page: (page + 1).toString(),
      per_page: size.toString(),
      ...(status && { status }),
    });
    const response = await api.get(`/admin/orders?${params}`);
    return response.data;
  }
);

export const fetchAdminOrderById = createAsyncThunk<any, number>(
  'admin/fetchOrderById',
  async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}`);
    return response.data.order;
  }
);

export const updateOrderStatus = createAsyncThunk<Order, { orderId: number; status: OrderStatus }>(
  'admin/updateOrderStatus',
  async ({ orderId, status }) => {
    const response = await api.put(`/admin/orders/${orderId}/status`, { status });
    return response.data.order || response.data;
  }
);

export const fetchAdminUsers = createAsyncThunk<any, { page?: number; size?: number }>(
  'admin/fetchUsers',
  async ({ page = 0, size = 10 }) => {
    const response = await api.get(`/admin/users?page=${page + 1}&per_page=${size}`);
    return response.data;
  }
);

const initialState: AdminState = {
  dashboardStats: null,
  products: [],
  orders: [],
  users: [],
  loading: false,
  error: null,
  totalPages: 0,
  totalElements: 0,
  productStatistics: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard stats';
      });

    // Dashboard Stats Filtered
    builder
      .addCase(fetchDashboardStatsFiltered.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardStatsFiltered.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStatsFiltered.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filtered dashboard stats';
      });

    // Admin Products
    builder
      .addCase(fetchAdminProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (Array.isArray(payload)) {
          // Direct array response
          state.products = payload;
          state.totalPages = 1;
          state.totalElements = payload.length;
          state.productStatistics = null;
        } else if (payload && payload.products) {
          // Backend format: {products: [...], pagination: {...}, statistics: {...}}
          state.products = payload.products;
          state.totalPages = payload.pagination?.last_page || 1;
          state.totalElements = payload.pagination?.total || payload.products.length;
          state.productStatistics = payload.statistics || null;
        } else if (payload && payload.content) {
          // Alternative paginated format: {content: [...], totalPages: ...}
          state.products = payload.content;
          state.totalPages = payload.totalPages || 0;
          state.totalElements = payload.totalElements || 0;
          state.productStatistics = null;
        } else {
          // Fallback for unexpected response format
          state.products = [];
          state.totalPages = 0;
          state.totalElements = 0;
          state.productStatistics = null;
        }
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      });

    // Create Product
    builder
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.unshift(action.payload);
      });

    // Fetch Product by ID
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        const existingIndex = state.products.findIndex(p => p.id === action.payload.id);
        if (existingIndex !== -1) {
          state.products[existingIndex] = action.payload;
        } else {
          state.products.push(action.payload);
        }
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch product';
      });

    // Update Product
    builder
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      });

    // Delete Product
    builder
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p.id !== action.payload);
      });

    // Admin Orders
    builder
      .addCase(fetchAdminOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (Array.isArray(payload)) {
          // Direct array response
          state.orders = payload;
          state.totalPages = 1;
          state.totalElements = payload.length;
        } else if (payload && payload.orders) {
          // Backend format: {orders: [...], pagination: {...}}
          // Transform backend data to frontend format
          state.orders = payload.orders.map((order: any) => ({
            id: order.id,
            orderNumber: order.order_number,
            user: order.customer_name ? transformApiUserToFrontend({
              id: 0,
              first_name: order.customer_name.split(' ')[0] || '',
              last_name: order.customer_name.split(' ').slice(1).join(' ') || '',
              email: order.customer_email,
              phone: '',
              role: 'USER' as UserRole,
              enabled: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }) : null,
            guestName: order.customer_name,
            guestEmail: order.customer_email,
            totalAmount: order.total_amount,
            status: order.status,
            paymentMethod: order.payment_method,
            createdAt: order.created_at,
            items: [],
            shippingAddress: {
              firstName: '',
              lastName: '',
              phone: '',
              address: '',
              city: '',
              postalCode: '',
              country: ''
            }
          }));
          state.totalPages = payload.pagination?.last_page || 1;
          state.totalElements = payload.pagination?.total || payload.orders.length;
        } else if (payload && payload.content) {
          // Alternative paginated format: {content: [...], totalPages: ...}
          state.orders = payload.content;
          state.totalPages = payload.totalPages || 0;
          state.totalElements = payload.totalElements || 0;
        } else {
          // Fallback for unexpected response format
          state.orders = [];
          state.totalPages = 0;
          state.totalElements = 0;
        }
      })
      .addCase(fetchAdminOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
      });

    // Fetch Order by ID
    builder
      .addCase(fetchAdminOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminOrderById.fulfilled, (state, action) => {
        state.loading = false;
        // Transform backend order data to frontend format
        const order = action.payload as any;
        const transformedOrder = {
          id: order.id,
          orderNumber: order.order_number,
          user: order.customer ? transformApiUserToFrontend({
            id: 0, // Temporary ID for guest customer
            first_name: order.customer.name.split(' ')[0] || '',
            last_name: order.customer.name.split(' ').slice(1).join(' ') || '',
            email: order.customer.email,
            phone: order.customer.phone,
            role: 'USER' as UserRole,
            enabled: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }) : null,
          guestName: order.customer?.name,
          guestEmail: order.customer?.email,
          guestPhone: order.customer?.phone,
          totalAmount: order.total_amount,
          status: order.status,
          paymentMethod: order.payment_method,
          createdAt: order.created_at,
          shippedDate: order.shipped_at,
          deliveredDate: order.delivered_at,
          notes: order.notes,
          items: order.items?.map((item: any) => ({
            id: item.id,
            quantity: item.quantity,
            price: item.price,
            totalPrice: item.total_price,
            product: {
              title: item.product_title,
              author: item.product_author,
              isbn: item.product_isbn
            }
          })) || [],
          shippingAddress: {
            address: order.shipping_address?.address_line_1 || '',
            city: order.shipping_address?.city || '',
            postalCode: order.shipping_address?.postal_code || '',
            country: order.shipping_address?.country || '',
            firstName: order.shipping_address?.first_name || '',
            lastName: order.shipping_address?.last_name || '',
            phone: order.shipping_address?.phone || ''
          }
        };
        
        // Update the order in the orders array or add if not exists
        const index = state.orders.findIndex(o => o.id === order.id);
        if (index !== -1) {
          state.orders[index] = transformedOrder;
        } else {
          state.orders.push(transformedOrder);
        }
      })
      .addCase(fetchAdminOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch order';
      });

    // Update Order Status
    builder
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const updatedOrder = action.payload as any;
        const index = state.orders.findIndex(o => o.id === updatedOrder.id);
        if (index !== -1) {
          // Only update the status, keep all other order data
          state.orders[index].status = updatedOrder.status;
        }
      });

    // Admin Users
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (Array.isArray(payload)) {
          // Direct array response
          state.users = payload;
          state.totalPages = 1;
          state.totalElements = payload.length;
        } else if (payload && payload.users) {
          // Backend format: {users: [...], pagination: {...}}
          // Transform users to frontend format
          state.users = payload.users.map((user: any) => transformApiUserToFrontend(user));
          state.totalPages = payload.pagination?.last_page || 1;
          state.totalElements = payload.pagination?.total || payload.users.length;
        } else if (payload && payload.content) {
          // Alternative paginated format: {content: [...], totalPages: ...}
          state.users = payload.content;
          state.totalPages = payload.totalPages || 0;
          state.totalElements = payload.totalElements || 0;
        } else {
          // Fallback for unexpected response format
          state.users = [];
          state.totalPages = 0;
          state.totalElements = 0;
        }
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      });
  },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;